import { useEffect, useState } from "react";
import { Loader2, Plus, PackagePlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/** 寄样管理 — put the picked 产品一览表 rows into a new or existing 寄样清单. */
export function AddToSampleListDialog({ open, onClose, overviewIds = [], onDone }) {
  const { t } = useLanguage();
  const [lists, setLists] = useState([]);
  const [name, setName] = useState("");
  const [customer, setCustomer] = useState("");
  const [receipt, setReceipt] = useState("");
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setCustomer("");
    setReceipt("");
    api.get("/sample-lists").then(({ data }) => setLists(data.items || [])).catch(() => {});
    api.get("/customers").then(({ data }) => setCustomers((data || []).map((c) => c.name).filter(Boolean))).catch(() => {});
  }, [open]);

  const addTo = async (listId) => {
    setBusy(listId);
    try {
      const { data } = await api.post(`/sample-lists/${listId}/items`, { overview_ids: overviewIds });
      toast.success(t("sample.added").replace("{n}", data.added).replace("{s}", data.skipped));
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const createAndAdd = async () => {
    if (!name.trim()) { toast.error(t("sample.nameRequired")); return; }
    setBusy("new");
    try {
      const { data } = await api.post("/sample-lists", { name: name.trim(), customer_name: customer.trim(), receipt_no: receipt.trim() });
      await addTo(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sample-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <PackagePlus size={15} /> {t("sample.addTitle").replace("{n}", String(overviewIds.length))}
          </DialogTitle>
        </DialogHeader>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.newName")}</Label>
          <div className="flex gap-2 mt-1">
            <Input value={name} autoFocus onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createAndAdd(); }}
              placeholder={t("sample.namePlaceholder")} className="h-9 text-sm" data-testid="sample-new-name" />
            <Button size="sm" className="h-9 shrink-0" onClick={createAndAdd} disabled={Boolean(busy)}
              data-testid="sample-create-btn">
              {busy === "new" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              {t("sample.createAndAdd")}
            </Button>
          </div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2 block">{t("sample.customer")}</Label>
          <Input value={customer} list="sample-customers" onChange={(e) => setCustomer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createAndAdd(); }}
            placeholder={t("sample.customerPlaceholder")} className="h-9 text-sm mt-1" data-testid="sample-new-customer" />
          <datalist id="sample-customers">{customers.map((c) => <option key={c} value={c} />)}</datalist>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2 block">{t("sample.receiptNo")}</Label>
          <Input value={receipt} onChange={(e) => setReceipt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createAndAdd(); }}
            placeholder={t("sample.receiptNoPlaceholder")} className="h-9 text-sm mt-1" data-testid="sample-new-receipt" />
        </div>

        {lists.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.orExisting")}</p>
            <div className="max-h-[220px] overflow-y-auto border border-border rounded-md divide-y divide-border/60">
              {lists.map((l) => (
                <button key={l.id} type="button" onClick={() => addTo(l.id)} disabled={Boolean(busy)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-secondary/60 transition-colors"
                  data-testid={`sample-pick-${l.id}`}>
                  <span className="text-sm truncate">{l.name}</span>
                  <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                    {l.customer_name ? `${l.customer_name} · ` : ""}{l.count} · {(l.created_by || "").split("@")[0]} · {(l.created_at || "").slice(0, 10)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{t("market.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
